import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth, ROLES } from './context/AuthContext'
import Layout from './components/Layout'

// Retry wrapper for lazy imports - handles chunk loading failures after deployment
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    )
    try {
      const component = await componentImport()
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false')
      return component
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true')
        window.location.reload()
      }
      throw error
    }
  })

// Lazy loaded pages for code splitting
const Login = lazyWithRetry(() => import('./pages/Login'))
const Products = lazyWithRetry(() => import('./pages/Products'))
const ProductDetail = lazyWithRetry(() => import('./pages/ProductDetail'))
const ProductForm = lazyWithRetry(() => import('./pages/ProductForm'))
const Warehouses = lazyWithRetry(() => import('./pages/Warehouses'))
const WarehouseForm = lazyWithRetry(() => import('./pages/WarehouseForm'))
const Receipts = lazyWithRetry(() => import('./pages/Receipts'))
const ReceiptForm = lazyWithRetry(() => import('./pages/ReceiptForm'))
const ReceiptDetail = lazyWithRetry(() => import('./pages/ReceiptDetail'))
const PlacementList = lazyWithRetry(() => import('./pages/Placement'))
const PlacementDetail = lazyWithRetry(() => import('./pages/Placement').then(m => ({ default: m.PlacementDetail })))
const Inventories = lazyWithRetry(() => import('./pages/Inventories'))
const InventoryDetail = lazyWithRetry(() => import('./pages/InventoryDetail'))
const StockMovement = lazyWithRetry(() => import('./pages/StockMovement'))
const Settings = lazyWithRetry(() => import('./pages/Settings'))
const Incidents = lazyWithRetry(() => import('./pages/Incidents'))
const IncidentDetail = lazyWithRetry(() => import('./pages/IncidentDetail'))
const DimensionRequests = lazyWithRetry(() => import('./pages/DimensionRequests'))
const Stock = lazyWithRetry(() => import('./pages/Stock'))
const Orders = lazyWithRetry(() => import('./pages/Orders'))
const OrderDetail = lazyWithRetry(() => import('./pages/OrderDetail'))
const OrderForm = lazyWithRetry(() => import('./pages/OrderForm'))
const PackingStation = lazyWithRetry(() => import('./pages/PackingStation'))
const PackingStations = lazyWithRetry(() => import('./pages/PackingStations'))
const PackingStationForm = lazyWithRetry(() => import('./pages/PackingStationForm'))
const LabelingStation = lazyWithRetry(() => import('./pages/LabelingStation'))
const Admin = lazyWithRetry(() => import('./pages/Admin'))
const RoleMatrix = lazyWithRetry(() => import('./pages/RoleMatrix'))
const Notifications = lazyWithRetry(() => import('./pages/Notifications'))
const AccountLinks = lazyWithRetry(() => import('./pages/AccountLinks'))
const Catalog = lazyWithRetry(() => import('./pages/Catalog'))
const ReceiptCalendar = lazyWithRetry(() => import('./pages/ReceiptCalendar'))
const Shipments = lazyWithRetry(() => import('./pages/Shipments'))
const ShipmentForm = lazyWithRetry(() => import('./pages/ShipmentForm'))
const ShipmentDetail = lazyWithRetry(() => import('./pages/ShipmentDetail'))
const CreateAccount = lazyWithRetry(() => import('./pages/CreateAccount'))
const LegalEntities = lazyWithRetry(() => import('./pages/LegalEntities'))
const AdminTerminalLogs = lazyWithRetry(() => import('./pages/AdminTerminalLogs'))
const Help = lazyWithRetry(() => import('./pages/Help'))
const Superadmin = lazyWithRetry(() => import('./pages/Superadmin'))
const BillingTariffs = lazyWithRetry(() => import('./pages/BillingTariffs'))
const BillingInvoices = lazyWithRetry(() => import('./pages/BillingInvoices'))
const BillingInvoiceDetail = lazyWithRetry(() => import('./pages/BillingInvoiceDetail'))
const TerminalLogin = lazyWithRetry(() => import('./pages/terminal/TerminalLogin'))
const TerminalMenu = lazyWithRetry(() => import('./pages/terminal/TerminalMenu'))
const TerminalReceipts = lazyWithRetry(() => import('./pages/terminal/TerminalReceipts'))
const TerminalReceiptProcess = lazyWithRetry(() => import('./pages/terminal/TerminalReceiptProcess'))
const TerminalPlacements = lazyWithRetry(() => import('./pages/terminal/TerminalPlacements'))
const TerminalPlacementProcess = lazyWithRetry(() => import('./pages/terminal/TerminalPlacementProcess'))
const TerminalOrders = lazyWithRetry(() => import('./pages/terminal/TerminalOrders'))
const TerminalOrderPick = lazyWithRetry(() => import('./pages/terminal/TerminalOrderPick'))
const TerminalPacking = lazyWithRetry(() => import('./pages/terminal/TerminalPacking'))
const TerminalPackingProcess = lazyWithRetry(() => import('./pages/terminal/TerminalPackingProcess'))
const TerminalMovement = lazyWithRetry(() => import('./pages/terminal/TerminalMovement'))
const WavePicking = lazyWithRetry(() => import('./pages/terminal/WavePicking'))
const TerminalInfo = lazyWithRetry(() => import('./pages/terminal/TerminalInfo'))
const Waves = lazyWithRetry(() => import('./pages/Waves'))
const WaveDetail = lazyWithRetry(() => import('./pages/WaveDetail'))
const LocationLabels = lazyWithRetry(() => import('./pages/LocationLabels'))
const LabelTemplates = lazyWithRetry(() => import('./pages/LabelTemplates'))
const WebAssemblyList = lazyWithRetry(() => import('./pages/WebAssembly'))
const WebAssemblyDetail = lazyWithRetry(() => import('./pages/WebAssembly').then(m => ({ default: m.WebAssemblyDetail })))

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-gray-500">Загрузка страницы...</div>
    </div>
  )
}

function PrivateRoute({ children, allowedRoles }) {
  const { user, loading, hasRole } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles && !hasRole(...allowedRoles)) {
    return <Navigate to="/orders" replace />
  }

  return <Layout>{children}</Layout>
}

function WarehouseOnlyRoute({ children, adminOnly = false }) {
  const { user, loading, isClientAccount, isAdmin } = useAuth()

  if (loading) {
    return <PageLoader />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (isClientAccount || (adminOnly && !isAdmin)) {
    return <Navigate to="/products" replace />
  }

  return <Layout>{children}</Layout>
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/products" replace />
  }

  return children
}

function TerminalPrivateRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <PageLoader />
  }

  if (!user) {
    return <Navigate to="/terminal" replace />
  }

  return children
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Terminal routes (TSD) */}
      <Route path="/terminal" element={<TerminalLogin />} />
      <Route
        path="/terminal/menu"
        element={
          <TerminalPrivateRoute>
            <TerminalMenu />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/receipts"
        element={
          <TerminalPrivateRoute>
            <TerminalReceipts />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/receipts/:id"
        element={
          <TerminalPrivateRoute>
            <TerminalReceiptProcess />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/placement"
        element={
          <TerminalPrivateRoute>
            <TerminalPlacements />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/placement/:id"
        element={
          <TerminalPrivateRoute>
            <TerminalPlacementProcess />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/assembly"
        element={
          <TerminalPrivateRoute>
            <TerminalOrders />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/assembly/:id"
        element={
          <TerminalPrivateRoute>
            <TerminalOrderPick />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/packing"
        element={
          <TerminalPrivateRoute>
            <TerminalPacking />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/packing/:id"
        element={
          <TerminalPrivateRoute>
            <TerminalPackingProcess />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/movement"
        element={
          <TerminalPrivateRoute>
            <TerminalMovement />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/wave-picking"
        element={
          <TerminalPrivateRoute>
            <WavePicking />
          </TerminalPrivateRoute>
        }
      />
      <Route
        path="/terminal/info"
        element={
          <TerminalPrivateRoute>
            <TerminalInfo />
          </TerminalPrivateRoute>
        }
      />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/products"
        element={
          <PrivateRoute>
            <Products />
          </PrivateRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <PrivateRoute>
            <ProductForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/products/:id"
        element={
          <PrivateRoute>
            <ProductDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <PrivateRoute>
            <ProductForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/warehouses"
        element={
          <PrivateRoute>
            <Warehouses />
          </PrivateRoute>
        }
      />
      <Route
        path="/warehouses/new"
        element={
          <WarehouseOnlyRoute adminOnly>
            <WarehouseForm />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/warehouses/:id/edit"
        element={
          <WarehouseOnlyRoute adminOnly>
            <WarehouseForm />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/packing-stations"
        element={
          <WarehouseOnlyRoute>
            <PackingStations />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/packing-stations/new"
        element={
          <WarehouseOnlyRoute>
            <PackingStationForm />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/packing-stations/:id/edit"
        element={
          <WarehouseOnlyRoute>
            <PackingStationForm />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/receipts"
        element={
          <WarehouseOnlyRoute>
            <Receipts />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/receipts/new"
        element={
          <WarehouseOnlyRoute>
            <ReceiptForm />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/receipts/calendar"
        element={
          <WarehouseOnlyRoute>
            <ReceiptCalendar />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/receipts/:id"
        element={
          <WarehouseOnlyRoute>
            <ReceiptDetail />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/placements"
        element={
          <WarehouseOnlyRoute>
            <PlacementList />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/placements/:id"
        element={
          <WarehouseOnlyRoute>
            <PlacementDetail />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/shipments"
        element={
          <PrivateRoute>
            <Shipments />
          </PrivateRoute>
        }
      />
      <Route
        path="/shipments/new"
        element={
          <PrivateRoute>
            <ShipmentForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/shipments/:id"
        element={
          <PrivateRoute>
            <ShipmentDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/incidents"
        element={
          <PrivateRoute>
            <Incidents />
          </PrivateRoute>
        }
      />
      <Route
        path="/incidents/:id"
        element={
          <PrivateRoute>
            <IncidentDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/dimension-requests"
        element={
          <WarehouseOnlyRoute>
            <DimensionRequests />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/stock"
        element={
          <PrivateRoute>
            <Stock />
          </PrivateRoute>
        }
      />
      <Route path="/stock/inventories" element={<WarehouseOnlyRoute><Inventories /></WarehouseOnlyRoute>} />
      <Route path="/stock/inventories/:id" element={<WarehouseOnlyRoute><InventoryDetail /></WarehouseOnlyRoute>} />
      <Route
        path="/stock/movement"
        element={
          <WarehouseOnlyRoute>
            <StockMovement />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <PrivateRoute>
            <Orders />
          </PrivateRoute>
        }
      />
      <Route
        path="/packing"
        element={
          <WarehouseOnlyRoute>
            <PackingStation />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/labeling"
        element={
          <WarehouseOnlyRoute>
            <LabelingStation />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/orders/new"
        element={
          <PrivateRoute allowedRoles={['admin', 'coordinator', 'client']}>
            <OrderForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <PrivateRoute>
            <OrderDetail />
          </PrivateRoute>
        }
      />
      {/* Billing */}
      <Route
        path="/billing/tariffs"
        element={
          <WarehouseOnlyRoute>
            <BillingTariffs />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/billing/invoices"
        element={
          <PrivateRoute>
            <BillingInvoices />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/invoices/:id"
        element={
          <PrivateRoute>
            <BillingInvoiceDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Admin />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <PrivateRoute>
            <RoleMatrix />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/terminal-logs"
        element={
          <PrivateRoute>
            <AdminTerminalLogs />
          </PrivateRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <PrivateRoute>
            <Notifications />
          </PrivateRoute>
        }
      />
      <Route
        path="/account-links"
        element={
          <PrivateRoute>
            <AccountLinks />
          </PrivateRoute>
        }
      />
      <Route
        path="/legal-entities"
        element={
          <PrivateRoute>
            <LegalEntities />
          </PrivateRoute>
        }
      />
      <Route
        path="/catalog"
        element={
          <PrivateRoute>
            <Catalog />
          </PrivateRoute>
        }
      />
      <Route
        path="/accounts/new"
        element={
          <PrivateRoute>
            <CreateAccount />
          </PrivateRoute>
        }
      />
      <Route
        path="/help"
        element={
          <PrivateRoute>
            <Help />
          </PrivateRoute>
        }
      />
      <Route
        path="/superadmin"
        element={
          <PrivateRoute>
            <Superadmin />
          </PrivateRoute>
        }
      />
      <Route
        path="/waves"
        element={
          <WarehouseOnlyRoute>
            <Waves />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/waves/:id"
        element={
          <WarehouseOnlyRoute>
            <WaveDetail />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/locations/labels"
        element={
          <PrivateRoute>
            <LocationLabels />
          </PrivateRoute>
        }
      />
      <Route
        path="/labels/templates"
        element={
          <PrivateRoute>
            <LabelTemplates />
          </PrivateRoute>
        }
      />
      <Route
        path="/assembly"
        element={
          <WarehouseOnlyRoute>
            <WebAssemblyList />
          </WarehouseOnlyRoute>
        }
      />
      <Route
        path="/assembly/:id"
        element={
          <WarehouseOnlyRoute>
            <WebAssemblyDetail />
          </WarehouseOnlyRoute>
        }
      />
      <Route path="/" element={<Navigate to="/products" replace />} />
      <Route path="*" element={<Navigate to="/products" replace />} />
    </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}
